export const TIER_STYLES = {
  S: { label: "Starter", bg: "#e6f6f6", color: "#0a7a78", border: "#bfe7e6" },
  M: { label: "Mid", bg: "#eef1fe", color: "#3b5bdb", border: "#cdd7fb" },
  L: {
    label: "Large",
    bg: "rgba(255,75,79,0.08)",
    color: "#ff4b4f",
    border: "rgba(255,75,79,0.2)",
  },
  T: { label: "Trial", bg: "#fff7e6", color: "#b8860b", border: "#f2e2b8" },
  U: { label: "Unlimited", bg: "#eafbef", color: "#1a9850", border: "#c3edd0" },
};

export const TIER_IDS = ["S", "M", "L"];

export const ADMIN_TABS = [
  { id: "rate-card", path: "/pricing/rate-card", label: "Rate card" },
  { id: "clients", path: "/pricing/clients", label: "Clients & contracts" },
  { id: "usage", path: "/pricing/usage", label: "Usage & statements" },
  { id: "audit", path: "/pricing/audit", label: "Audit & access" },
];

export const MODEL_PROVIDERS = ["OpenAI", "Gemini", "Claude", "Perplexity"];

export const METER_RATE_GROUPS = [
  {
    id: "core",
    label: "Core",
    rows: [
      { id: "persona", action: "Persona generation", trigger: "/persona" },
      { id: "query-gen", action: "Query generation", trigger: "/queries" },
      {
        id: "simulation",
        action: "Full simulation / per re-run (per LLM)",
        trigger: "/simulate · per LLM run",
      },
    ],
  },
  {
    id: "additional",
    label: "Additional features",
    rows: [
      { id: "translate", action: "Results translation", trigger: "/translate" },
    ],
  },
];

export const MODIFY_DETAILS_METER_RATES = METER_RATE_GROUPS.flatMap(
  (group) => group.rows,
);

export const PUBLISH_CONFIG_OPTIONS = [
  { id: "plan-templates", label: "Plan templates", section: "Plan Cards" },
  { id: "models-per-tier", label: "Models per tier", section: "Plan Cards" },
  { id: "meter-rates", label: "Meter rates", section: "Meter Rates" },
];

// All three groups ticked by default, so saved prices also take effect.
export const DEFAULT_PUBLISH_CONFIG = ["plan-templates", "models-per-tier", "meter-rates"];

export const CAPACITY_ACTIONS = [
  { key: "simulation", label: "Full simulations" },
  { key: "persona", label: "Persona generations" },
  { key: "query-gen", label: "Query generations" },
  { key: "translate", label: "Results translations" },
];

export const TIER_COLORS = { S: "#0a7a78", M: "#3b5bdb", L: "#ff4b4f" };

export const LEDGER_ROWS = [
  {
    timestamp: "2026-06-15 09:41:22.118",
    agency: "Mediaplus DE",
    actor: "u-204",
    action: "Full simulation",
    credits: "-1",
    tier: "L",
    ref: "evt-8841",
  },
  {
    timestamp: "2026-06-15 09:41:22.140",
    agency: "Mediaplus DE",
    actor: "u-204",
    action: "↳ Persona generation",
    credits: "-1",
    tier: "L",
    ref: "↳ evt-8841",
  },
  {
    timestamp: "2026-06-14 23:59:59.900",
    agency: "Lorenz",
    actor: "system",
    action: "Period-end forfeit",
    credits: "-6",
    tier: "S",
    ref: "evt-8837",
  },
  {
    timestamp: "2026-06-14 18:03:11.220",
    agency: "Mediaplus DE",
    actor: "j.woerner",
    action: "Top-up order #2241",
    credits: "+40",
    tier: "L",
    ref: "ord-2241",
  },
  {
    timestamp: "2026-06-14 17:40:10.052",
    agency: "Mediaplus DE",
    actor: "f.kroll",
    action: "Grant",
    credits: "+10",
    tier: "L",
    ref: "—",
  },
];

export function parseEuroNumber(value) {
  if (value == null || value === "") return 0;
  const normalized = String(value).replace(/\./g, "").replace(",", ".");
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}

export function parseCreditNumber(value) {
  if (value == null || value === "") return 0;
  const normalized = String(value).replace(",", ".");
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}

export function formatEuroPerCredit(packageMo, credits) {
  const pkg = parseEuroNumber(packageMo);
  const cr = parseCreditNumber(credits);
  if (!cr) return "—";
  const per = pkg / cr;
  const [whole, frac = "00"] = per.toFixed(2).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `€${grouped},${frac}`;
}

export function formatCreditDecimal(n) {
  return (Math.round(n * 100) / 100).toString().replace(".", ",");
}

export function countModelsEnabled(modelToggles, tierId) {
  return MODEL_PROVIDERS.filter((provider) => modelToggles[provider]?.[tierId])
    .length;
}

export function computeTierCapacity({
  tierId,
  planTier,
  meterRates,
  modelToggles,
}) {
  const included = parseCreditNumber(planTier.includedCredits);
  const modelsOn = countModelsEnabled(modelToggles, tierId);
  const totalModels = MODEL_PROVIDERS.length;
  const simRate = parseCreditNumber(meterRates.simulation?.[tierId]);
  const personaRate = parseCreditNumber(meterRates.persona?.[tierId]);
  const queryRate = parseCreditNumber(meterRates["query-gen"]?.[tierId]);
  const translateRate = parseCreditNumber(meterRates.translate?.[tierId]);
  const simCost = simRate * modelsOn;

  const div = (cost) => (cost > 0 ? Math.floor(included / cost) : null);

  return {
    included: Math.round(included),
    simulation: div(simCost),
    persona: div(personaRate),
    "query-gen": div(queryRate),
    translate: div(translateRate),
    modelsLabel:
      modelsOn === totalModels
        ? `all ${totalModels}`
        : `${modelsOn} of ${totalModels}`,
    perCredit: formatEuroPerCredit(
      planTier.packageMo,
      planTier.includedCredits,
    ),
    note: `${modelsOn} of ${totalModels} models active · ${formatCreditDecimal(simCost)} cr / full simulation`,
  };
}
